import { useEffect, useState } from "react";
import adminApi from "../../api/adminApi";
import { toast } from "react-toastify";
import {
  PlusCircle, Trash2, Save, X, Pencil, ExternalLink,
  GripVertical, Eye, EyeOff, Loader2, PlayCircle, AlertTriangle,
} from "lucide-react";

export default function AdminLoginTutorials() {
  const [tutorials, setTutorials] = useState([]);
  const [loading,   setLoading]   = useState(true);

  const [newText, setNewText] = useState("");
  const [newUrl,  setNewUrl]  = useState("");
  const [adding,  setAdding]  = useState(false);

  const [editingId, setEditingId] = useState(null);
  const [editText,  setEditText]  = useState("");
  const [editUrl,   setEditUrl]   = useState("");
  const [editSort,  setEditSort]  = useState("0");
  const [saving,    setSaving]    = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting,     setDeleting]     = useState(false);

  const fetchTutorials = async () => {
    setLoading(true);
    try {
      const res = await adminApi.get("/login-tutorials");
      if (res.data.success) setTutorials(res.data.tutorials || []);
    } catch {
      toast.error("Failed to load tutorials");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTutorials(); }, []);

  const validateUrl = (url) => {
    try { new URL(url.trim()); return true; }
    catch { return false; }
  };

  const handleAdd = async () => {
    if (!newText.trim())      return toast.error("Enter the tutorial text");
    if (!newUrl.trim())       return toast.error("Enter the video URL");
    if (!validateUrl(newUrl)) return toast.error("Enter a valid URL (include https://)");

    setAdding(true);
    try {
      const res = await adminApi.post("/login-tutorials", {
        text: newText.trim(),
        url:  newUrl.trim(),
        sortOrder: tutorials.length,
      });
      if (res.data.success) {
        toast.success("Tutorial added");
        setNewText("");
        setNewUrl("");
        fetchTutorials();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to add tutorial");
    } finally {
      setAdding(false);
    }
  };

  const startEdit = (t) => {
    setEditingId(t._id);
    setEditText(t.text);
    setEditUrl(t.url);
    setEditSort(String(t.sortOrder ?? 0));
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditText("");
    setEditUrl("");
    setEditSort("0");
  };

  const saveEdit = async (id) => {
    if (!editText.trim())      return toast.error("Text cannot be empty");
    if (!editUrl.trim())       return toast.error("URL cannot be empty");
    if (!validateUrl(editUrl)) return toast.error("Enter a valid URL (include https://)");

    setSaving(true);
    try {
      const res = await adminApi.put(`/login-tutorials/${id}`, {
        text: editText.trim(),
        url:  editUrl.trim(),
        sortOrder: Number(editSort) || 0,
      });
      if (res.data.success) {
        toast.success("Tutorial updated");
        cancelEdit();
        fetchTutorials();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update tutorial");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (t) => {
    try {
      const res = await adminApi.put(`/login-tutorials/${t._id}`, { isActive: !t.isActive });
      if (res.data.success) {
        toast.success(t.isActive ? "Hidden from users" : "Now visible to users");
        fetchTutorials();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update tutorial");
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await adminApi.delete(`/login-tutorials/${deleteTarget._id}`);
      if (res.data.success) {
        toast.success("Tutorial deleted");
        setDeleteTarget(null);
        fetchTutorials();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to delete tutorial");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <h1 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <PlayCircle size={20} className="text-purple-600 dark:text-purple-400" />
          Login Tutorials
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
          Manage the tutorial links shown to users in "My Purchases" after they buy an account.
          Only the text is visible to users — clicking it opens the video URL automatically.
        </p>
      </div>

      {/* Add new tutorial */}
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5 space-y-4">
        <p className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
          <PlusCircle size={15} className="text-purple-600 dark:text-purple-400" />
          Add New Tutorial
        </p>
        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
            Text shown to users
          </label>
          <input
            type="text"
            value={newText}
            onChange={(e) => setNewText(e.target.value)}
            placeholder="e.g. How to login a Facebook account"
            disabled={adding}
            className="w-full px-3 py-2.5 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 dark:text-white"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
            Video URL <span className="text-gray-400 font-normal">(hidden from users)</span>
          </label>
          <input
            type="text"
            value={newUrl}
            onChange={(e) => setNewUrl(e.target.value)}
            placeholder="https://youtube.com/watch?v=..."
            disabled={adding}
            className="w-full px-3 py-2.5 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 dark:text-white"
          />
        </div>
        <button
          onClick={handleAdd}
          disabled={adding}
          className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-purple-600 hover:bg-purple-700 disabled:bg-purple-300 rounded-lg transition"
        >
          {adding ? <Loader2 size={15} className="animate-spin" /> : <PlusCircle size={15} />}
          {adding ? "Adding..." : "Add Tutorial"}
        </button>
      </div>

      {/* List */}
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-800">
          <p className="text-sm font-semibold text-gray-900 dark:text-white">
            All Tutorials ({tutorials.length})
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-14">
            <Loader2 size={22} className="animate-spin text-purple-500" />
          </div>
        ) : tutorials.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-14 px-6 text-center">
            <PlayCircle size={32} className="text-gray-300 dark:text-gray-600 mb-2" />
            <p className="text-sm text-gray-500 dark:text-gray-400">No tutorials yet — add your first one above.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-gray-800">
            {tutorials.map((t) => {
              const isEditing = editingId === t._id;
              return (
                <div key={t._id} className="p-4">
                  {isEditing ? (
                    <div className="space-y-3">
                      <div className="flex gap-2">
                        <div className="flex-1">
                          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Text</label>
                          <input
                            type="text"
                            value={editText}
                            onChange={(e) => setEditText(e.target.value)}
                            className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 dark:text-white"
                          />
                        </div>
                        <div className="w-20">
                          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Order</label>
                          <input
                            type="number"
                            value={editSort}
                            onChange={(e) => setEditSort(e.target.value)}
                            className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 dark:text-white"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">URL</label>
                        <input
                          type="text"
                          value={editUrl}
                          onChange={(e) => setEditUrl(e.target.value)}
                          className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 dark:text-white"
                        />
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => saveEdit(t._id)}
                          disabled={saving}
                          className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-white bg-purple-600 hover:bg-purple-700 disabled:bg-purple-300 rounded-lg transition"
                        >
                          {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                          Save
                        </button>
                        <button
                          onClick={cancelEdit}
                          disabled={saving}
                          className="flex items-center gap-1.5 px-4 py-2 text-xs font-medium text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition"
                        >
                          <X size={13} />
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start gap-3">
                      <GripVertical size={15} className="text-gray-300 dark:text-gray-600 flex-shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            {t.text}
                          </p>
                          <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                            t.isActive
                              ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300"
                              : "bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400"
                          }`}>
                            {t.isActive ? "Visible" : "Hidden"}
                          </span>
                          <span className="text-xs text-gray-400 dark:text-gray-500">
                            Order: {t.sortOrder ?? 0}
                          </span>
                        </div>
                        <a
                          href={t.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-xs text-purple-600 dark:text-purple-400 hover:underline mt-1 break-all"
                        >
                          <ExternalLink size={11} className="flex-shrink-0" />
                          {t.url}
                        </a>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                          onClick={() => toggleActive(t)}
                          title={t.isActive ? "Hide from users" : "Show to users"}
                          className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition"
                        >
                          {t.isActive
                            ? <Eye size={15} className="text-gray-500" />
                            : <EyeOff size={15} className="text-gray-400" />
                          }
                        </button>
                        <button
                          onClick={() => startEdit(t)}
                          title="Edit"
                          className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition"
                        >
                          <Pencil size={15} className="text-gray-500" />
                        </button>
                        <button
                          onClick={() => setDeleteTarget(t)}
                          title="Delete"
                          className="p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition"
                        >
                          <Trash2 size={15} className="text-red-500" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {deleteTarget && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 px-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-5 max-w-sm w-full text-center shadow-2xl">
            <AlertTriangle size={28} className="mx-auto text-red-500 mb-3" />
            <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Delete this tutorial?</h2>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-5 break-words">
              "{deleteTarget.text}" will be removed and no longer shown to users. This cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteTarget(null)}
                disabled={deleting}
                className="flex-1 py-2.5 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg text-sm font-medium transition"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleting}
                className="flex-1 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium transition flex items-center justify-center gap-2"
              >
                {deleting ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}